import React, { useEffect, useMemo, useState } from "react";
import { Option } from "react-select/lib/filters";
import { useTranslation } from "react-i18next";
import { Checkbox, Tag, Heading, Divider } from "@foris/avocado-suite";
import SelectLabels from "@dimensions/group/EditLabels/SelectLabels";
import LabelCategorySelector from "@modules/shared/selectors/Selectors/LabelCategoriesSelector";
import { ILabel } from "@dimensions/group/EditLabels/ContextEditLabels";
import {
  setAdvancedSearchLabelCategory,
  setAdvancedSearchSelectedLabels,
  useAdvancedSearchStore,
} from "@context/useAdvancedSearchStore";
import css from "./advancedLabelsSelectors.module.scss";

interface AdvancedLabelsSelectorsProps {
  scenario: string;
  contentType: "group" | "package" | "all";
  onChange?: (payload: { categoryId?: string | null; labelsIds: string[] }) => void;
}

const AdvancedLabelsSelectors = ({
  scenario,
  contentType,
  onChange,
}: AdvancedLabelsSelectorsProps) => {
  // States for handling the visibility of the category and label selectors
  const selectedLabels = useAdvancedSearchStore(state => state.selectedLabels);
  const labelCategory = useAdvancedSearchStore(state => state.labelCategory);
  const categoryId = labelCategory?.value ?? null;
  const [showCategorySelector, setShowCategorySelector] = useState(!!labelCategory?.value);
  const [showLabelsSelector, setShowLabelsSelector] = useState(
    !!Object.values(selectedLabels)?.length,
  );

  // Hooks
  const { t } = useTranslation();

  const handleShowCategorySelector = (isVisible: boolean) => {
    setAdvancedSearchLabelCategory(null);
    setShowCategorySelector(isVisible);
  };

  const handleShowLabelsSelector = (isVisible: boolean) => {
    setAdvancedSearchSelectedLabels({});
    setShowLabelsSelector(isVisible);
  };

  const handleSetCategoryId = (category?: Option | null) => {
    setAdvancedSearchSelectedLabels({});
    setAdvancedSearchLabelCategory(category ?? null);
  };

  const handleSetSelectedLabels = (label: ILabel) => {
    if (selectedLabels?.[label.id]) {
      return;
    }

    setAdvancedSearchSelectedLabels({ ...selectedLabels, [label.id]: label });
  };

  const handleRemoveSelectedLabel = (labelId: string) => {
    const newLabels = structuredClone(selectedLabels);
    newLabels?.[labelId] && delete newLabels[labelId];

    setAdvancedSearchSelectedLabels(newLabels);
  };

  useEffect(() => {
    onChange?.({ categoryId, labelsIds: Object.values(selectedLabels)?.map(({ id }) => id) });
  }, [selectedLabels, categoryId]);

  const labelList = useMemo(() => {
    return Object.values(selectedLabels).map(({ id, name, code }) => (
      <Tag key={id} type="outlined" onIconClick={() => handleRemoveSelectedLabel(id)} icon="close">
        {name}
        {code ? ` | ${code}` : ""}
      </Tag>
    ));
  }, [selectedLabels]);

  return (
    <div className={css.advancedLabelsSelectors}>
      <Heading className={css.advancedLabelsSelectors_title} type="h4">
        {t("advanced-search.package.forms.advanced-filters.title")}
      </Heading>
      <div className={css.advancedLabelsSelectors_control}>
        <Checkbox
          defaultChecked={!!labelCategory?.value}
          labelRight={t("advanced-search.package.forms.advanced-filters.checkbox-type-tag.label")}
          onChange={e => handleShowCategorySelector(e?.target?.checked)}
        />
        {showCategorySelector && (
          <LabelCategorySelector
            scenario={scenario}
            value={labelCategory}
            onChange={handleSetCategoryId}
          />
        )}
      </div>

      <div className={css.advancedLabelsSelectors_control}>
        <Checkbox
          defaultChecked={!!labelList?.length}
          labelRight={t("advanced-search.package.forms.advanced-filters.checkbox-tag.label")}
          onChange={e => handleShowLabelsSelector(e?.target?.checked)}
        />
        {showLabelsSelector && (
          <SelectLabels
            key={`labels-${categoryId}`}
            className={css.labelsSelector}
            labelCategoryId={categoryId}
            contentType={contentType}
            onSelectLabel={({ value }) => handleSetSelectedLabels(value)}
          />
        )}
      </div>

      {!!labelList?.length && <Divider className={css.advancedLabelsSelectors_divider} />}

      {!!labelList?.length && <div className={css.advancedLabelsSelectors_labels}>{labelList}</div>}
    </div>
  );
};

export default AdvancedLabelsSelectors;
